import React, { useState, useRef, useEffect } from 'react';
import { FaColumns } from 'react-icons/fa'; // Icon for the button

const ColumnSelector = ({ allColumns, visibleColumns, onVisibleColumnsChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Create a map for quick lookup of visible column accessors
  const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor));

  const handleToggle = (columnAccessor) => {
    const isVisible = visibleAccessors.has(columnAccessor);
    let newVisibleColumns;

    if (isVisible) {
      // Remove the column
      newVisibleColumns = visibleColumns.filter((col) => col.accessor !== columnAccessor);
    } else {
      // Add the column - find its definition in allColumns
      const columnToAdd = allColumns.find((col) => col.accessor === columnAccessor);
      if (columnToAdd) {
        // Try to insert it back in a reasonable order (or just append)
        // For simplicity, we'll append here. A more complex logic could try to maintain original order.
        newVisibleColumns = [...visibleColumns, columnToAdd];
      } else {
        newVisibleColumns = visibleColumns; // Should not happen if logic is correct
      }
    }
    onVisibleColumnsChange(newVisibleColumns);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div>
        <button
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-surface text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          id="options-menu"
          aria-haspopup="true"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        >
          <FaColumns className="mr-2 -ml-1 h-5 w-5" aria-hidden="true" />
          Columns
        </button>
      </div>

      {isOpen && (
        <div
          className="origin-top-right absolute right-0 mt-2 w-64 rounded-lg shadow-2xl bg-surface border-2 border-gray-200 focus:outline-none z-50 max-h-96 overflow-y-auto"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
          style={{
            boxShadow:
              '0 10px 25px rgb(var(--shadow-rgb) / var(--shadow-a-1)), 0 6px 10px rgb(var(--shadow-rgb) / var(--shadow-a-2))',
          }}
        >
          <div className="py-2" role="none">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Select Columns
              </p>
            </div>
            {allColumns.map((column) =>
              // Ensure column has a unique accessor and a Header to display
              column.accessor && column.Header ? (
                <div
                  key={column.accessor}
                  className="flex items-center px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleToggle(column.accessor)}
                >
                  <input
                    type="checkbox"
                    id={`col-${column.accessor}`}
                    checked={visibleAccessors.has(column.accessor)}
                    onChange={() => handleToggle(column.accessor)}
                    className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <label
                    htmlFor={`col-${column.accessor}`}
                    className="text-gray-700 select-none cursor-pointer flex-1"
                  >
                    {/* Render Header which might be a string or a function/component */}
                    {typeof column.Header === 'function' ? column.Header({}) : column.Header}
                  </label>
                </div>
              ) : null // Skip columns without accessor or Header
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSelector;
